import { useEffect, useRef, useState, type MutableRefObject } from 'react';
import { getGlobalDefaultPageSize } from './table-state.js';

/**
 * Hook that listens for preference changes and updates page size.
 * 
 * This hook solves the timing issue where tables initialize before
 * preferences are loaded from the server. It allows tables to:
 * 1. Use an initial page size from localStorage if available
 * 2. Update dynamically when preferences change (after server load)
 * 
 * The `onPageSizeChange` callback will be called when:
 * - The `finance:preferences:changed` event fires (preferences loaded/updated)
 * - The new page size is different from the current one
 * 
 * Usage:
 * ```tsx
 * const currentPageSizeRef = useRef(25);
 * currentPageSizeRef.current = state.pagination.pageSize;
 * usePreferencesPageSize(currentPageSizeRef, (newPageSize) => {
 *   handlePageSizeChange(newPageSize);
 * });
 * ```
 */
export function usePreferencesPageSize(
  currentPageSizeRef: MutableRefObject<number>,
  onPageSizeChange: (newPageSize: number) => void,
): void {
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    // On first mount, check if preferences are available and apply if different
    if (!initialized) {
      const globalPageSize = getGlobalDefaultPageSize();
      if (globalPageSize && globalPageSize !== currentPageSizeRef.current) {
        onPageSizeChange(globalPageSize);
      }
      setInitialized(true);
    }

    // Listen for future preference changes from the server
    // (when preferences are loaded from backend or user updates preferences)
    const handlePreferenceChange = (event: Event) => {
      const detail = (event as CustomEvent<{ defaultPageSize?: number }>).detail ?? {};
      const newPageSize = detail.defaultPageSize;
      
      if (newPageSize && Number.isSafeInteger(newPageSize) && newPageSize > 0) {
        if (newPageSize !== currentPageSizeRef.current) {
          onPageSizeChange(newPageSize);
        }
      }
    };

    window.addEventListener('finance:preferences:changed', handlePreferenceChange);
    return () => window.removeEventListener('finance:preferences:changed', handlePreferenceChange);
  }, [initialized, onPageSizeChange, currentPageSizeRef]);
}
